import { Overlay } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { DialogPosition, MatDialog } from '@angular/material/dialog';

import { getUsersLoggedIn, hasAnonymization, UsersList } from '@collections/users';
import { SharedResourcesType } from '@core/shared-resources/shared-resources.model';
import {
  NotesModalComponent,
  NotesModalComponentData,
} from '@modules/notes/components/modal/notes-modal.component';
import { selectActiveNotesFilters } from '@modules/notes/filters-store/notes-filters.selectors';
import { getNotesModalState } from '@modules/notes/store/notes.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  mojoNotesLoad,
  mojoNotesCreate,
  mojoNotesLoadSuccess,
  mojoNotesHiringLoadSuccess,
  mojoNotesReset,
} from 'mojo-irp-notes';
import { of } from 'rxjs';
import { map, switchMap, withLatestFrom, concatMap } from 'rxjs/operators';
import { NoteService } from '../services/note.service';
import { notesCreate, notesLoad, notesOpenModal, notesOpenModalSuccess } from './notes.actions';
import { sharedResourcesStoreCurrent } from '@core/shared-resources/store/shared-resources.actions';

@Injectable()
export class NotesEffects {
  load = createEffect(() =>
    this.actions$.pipe(
      ofType(notesLoad),
      withLatestFrom(
        this.store.select(selectActiveNotesFilters),
        this.store.select(hasAnonymization),
        this.store.select(getUsersLoggedIn).pipe(map((user) => user?.id)),
      ),
      concatMap(([_, notesFilters, anonymised, loggedInUserId]) => [
        mojoNotesReset(),
        mojoNotesLoad({ anonymised, loggedInUserId, notesFilters }),
      ]),
    ),
  );

  loadSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(mojoNotesHiringLoadSuccess, mojoNotesLoadSuccess),
      map(({ userIds }) => new UsersList({ userIds })),
    ),
  );

  save = createEffect(() =>
    this.actions$.pipe(
      ofType(notesCreate),
      withLatestFrom(this.store.select(selectActiveNotesFilters)),
      map(([{ payload }, notesFilters]) => mojoNotesCreate({ payload, notesFilters })),
    ),
  );

  openModal = createEffect(() =>
    this.actions$.pipe(
      ofType(notesOpenModal),
      withLatestFrom(this.store.select(getNotesModalState)),
      switchMap(([{ data }, isModalAlreadyOpened]) => {
        if (!isModalAlreadyOpened) {
          const dialogPosition: DialogPosition = {
            bottom: '0',
            right: '17rem',
          };
          this.dialog.open<NotesModalComponent, NotesModalComponentData>(NotesModalComponent, {
            panelClass: ['medium-size-modal', 'notes-modal'],
            disableClose: true,
            position: dialogPosition,
            autoFocus: 'false',
            width: '35rem',
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            data,
          });
        }
        return of(
          notesOpenModalSuccess({ noteId: data.note?.id, teamMembers: data.note?.teamMembers }),
        );
      }),
    ),
  );

  setCurrentSharedResource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(notesOpenModalSuccess),
      withLatestFrom(this.store.select(getUsersLoggedIn).pipe(map((user) => user?.id))),
      switchMap(([{ noteId, teamMembers }, loggedInId]) =>
        of(
          sharedResourcesStoreCurrent({
            resourceId: noteId,
            resourceType: SharedResourcesType.note,
            teamMembers: teamMembers
              ? teamMembers.filter((teamMember) => teamMember.id !== loggedInId)
              : [],
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private dialog: MatDialog,
    private overlay: Overlay,
  ) {}
}
